import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { crm } from "../../theme";
import { getBoard } from "../../services/crmService";
import { getCompanies } from "../../services/companyService";

export interface QuoteFormValues {
  number: string;
  clientName: string;
  prospectId?: string | null;
  companyId?: string | null;
  validUntil?: string | null; // yyyy-MM-dd
  notes?: string;
}

export interface Option {
  id: string;
  label: string;
  sub?: string;
}

/** Busca simples com lista: escolhe um item (lead ou empresa) ou limpa. */
export const EntitySearch = ({
  label,
  placeholder,
  options,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: Option[];
  value?: string | null;
  onChange: (option: Option | null) => void;
}) => {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);

  const matches = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return options.slice(0, 8);
    return options.filter((o) => `${o.label} ${o.sub ?? ""}`.toLowerCase().includes(t)).slice(0, 8);
  }, [options, term]);

  return (
    <Box position="relative">
      <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
        {label}
      </Text>
      {selected ? (
        <Flex align="center" justify="space-between" border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} px="10px" py="6px" bg={crm.subtle}>
          <Text fontSize="14px" noOfLines={1}>
            {selected.label}
          </Text>
          <Button size="xs" variant="ghost" onClick={() => onChange(null)}>
            Remover
          </Button>
        </Flex>
      ) : (
        <>
          <Input
            size="sm"
            bg="white"
            value={term}
            placeholder={placeholder}
            onChange={(e) => {
              setTerm(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {open && matches.length > 0 && (
            <Box
              position="absolute"
              zIndex={20}
              left={0}
              right={0}
              mt="2px"
              bg="white"
              border={`1px solid ${crm.border}`}
              borderRadius={crm.radiusSm}
              boxShadow={crm.shadowMd}
              maxH="200px"
              overflowY="auto"
            >
              {matches.map((o) => (
                <Box
                  key={o.id}
                  px="10px"
                  py="6px"
                  cursor="pointer"
                  _hover={{ bg: crm.brandSoft }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(o);
                    setTerm("");
                    setOpen(false);
                  }}
                >
                  <Text fontSize="13px">{o.label}</Text>
                  {o.sub && (
                    <Text fontSize="11px" color={crm.textFaint}>
                      {o.sub}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Modal do orçamento (Simulador): número, lead, cliente (empresa), validade e notas.
 * Serve para criar ("Salvar como orçamento") e para editar os dados de um orçamento aberto.
 */
const SaveQuoteModal = ({
  isOpen,
  onClose,
  initial,
  editing,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  initial: QuoteFormValues;
  /** Editando um orçamento existente: número fica travado. */
  editing?: boolean;
  onSubmit: (values: QuoteFormValues) => Promise<string[] | null>;
}) => {
  const [values, setValues] = useState<QuoteFormValues>(initial);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState<Option[]>([]);
  const [companies, setCompanies] = useState<Option[]>([]);

  useEffect(() => {
    if (isOpen) {
      setValues(initial);
      setErrors([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || leads.length || companies.length) return;
    getBoard().then((columns) =>
      setLeads(
        columns
          .flatMap((c) => c.leads)
          .filter((l) => l.id)
          .map((l) => ({ id: l.id!, label: l.tradeName || l.name || "Lead", sub: l.cnpj || l.email }))
      )
    );
    getCompanies(true).then((list) =>
      setCompanies(
        (Array.isArray(list) ? list : [])
          .filter((c: any) => c.id)
          .map((c: any) => ({ id: c.id, label: c.name, sub: c.cnpj }))
      )
    );
  }, [isOpen, leads.length, companies.length]);

  const patch = (next: Partial<QuoteFormValues>) => setValues((v) => ({ ...v, ...next }));

  const submit = async () => {
    setSaving(true);
    const problems = await onSubmit(values);
    setSaving(false);
    if (problems) setErrors(problems);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={crm.radius}>
        <ModalHeader fontSize="18px">{editing ? "Dados do orçamento" : "Salvar como orçamento"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap="12px">
            <Box>
              <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
                Número
              </Text>
              <Input size="sm" value={values.number} isDisabled={editing} onChange={(e) => patch({ number: e.target.value })} maxLength={40} />
            </Box>
            <EntitySearch
              label="Lead (opcional)"
              placeholder="Buscar lead por nome ou CNPJ"
              options={leads}
              value={values.prospectId}
              onChange={(o) => patch({ prospectId: o?.id ?? null, clientName: values.clientName || o?.label || "" })}
            />
            <EntitySearch
              label="Cliente cadastrado (opcional)"
              placeholder="Buscar empresa por nome ou CNPJ"
              options={companies}
              value={values.companyId}
              onChange={(o) => patch({ companyId: o?.id ?? null, clientName: o?.label ?? values.clientName })}
            />
            <Box>
              <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
                Nome do cliente
              </Text>
              <Input size="sm" value={values.clientName} onChange={(e) => patch({ clientName: e.target.value })} maxLength={200} />
            </Box>
            <Box>
              <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
                Validade
              </Text>
              <Input size="sm" type="date" value={values.validUntil ?? ""} onChange={(e) => patch({ validUntil: e.target.value || null })} />
            </Box>
            <Box>
              <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
                Notas
              </Text>
              <Textarea size="sm" rows={3} value={values.notes ?? ""} onChange={(e) => patch({ notes: e.target.value })} />
            </Box>
            {errors.length > 0 && (
              <Box bg="#fef2f2" border="1px solid #fecaca" borderRadius={crm.radiusSm} p="10px">
                {errors.map((e) => (
                  <Text key={e} fontSize="13px" color={crm.danger}>
                    {e}
                  </Text>
                ))}
              </Box>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} onClick={submit}>
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaveQuoteModal;
